import type { VehicleGraphData } from '@packages/domain/cvt';
import { PlotParams } from 'react-plotly.js';
import Plotly, { Shape } from 'plotly.js-basic-dist-min';

const plotRange = 20;

export type DefectKey =
    | 'chainSlip'
    | 'frontWheelSpeed'
    | 'fwdClutchSlip'
    | 'inputClutchSlip'
    | 'lockUpDutyRatio'
    | 'luJudder'
    | 'luYusa'
    | 'primaryUpDuty'
    | 'chainSlipTR58'
    | 'luYusaTR58';

export interface CvtPlotConfig extends PlotParams {
    defect: DefectKey;
}

const DEFECT_LABELS: Record<DefectKey, string> = {
    chainSlip: 'Chain Slip',
    frontWheelSpeed: 'Front Wheel Speed',
    fwdClutchSlip: 'FWD Clutch Slip',
    inputClutchSlip: 'Input Clutch Slip',
    lockUpDutyRatio: 'Lock-Up Duty Ratio',
    luJudder: 'Lock Up Judder',
    luYusa: 'Shock After Lock Up',
    primaryUpDuty: 'Primary Up Duty',
    chainSlipTR58: 'Chain Slip',
    luYusaTR58: 'Shock After Lock Up',
};

type DefectItems = {
    index: number;
    TIME: number;
};
type DefectData = Record<DefectKey, DefectItems[]>;

function filterCloseTimes(arr: DefectItems[]) {
    if (arr.length === 0) return arr;
    const result = [arr[0]];
    for (let i = 1; i < arr.length; i++) {
        if (arr[i].TIME - result[result.length - 1].TIME > plotRange) {
            result.push(arr[i]);
        }
    }
    return result;
}

function defectDataPoint(data: VehicleGraphData[]): DefectData {
    const defects: DefectKey[] = [
        'chainSlip',
        'luJudder',
        'luYusa',
        'fwdClutchSlip',
        'inputClutchSlip',
        'chainSlipTR58',
        'luYusaTR58',
    ];
    const defectData: DefectData = {
        lockUpDutyRatio: [],
        frontWheelSpeed: [],
        primaryUpDuty: [],
        chainSlip: [],
        luJudder: [],
        luYusa: [],
        fwdClutchSlip: [],
        inputClutchSlip: [],
        chainSlipTR58: [],
        luYusaTR58: [],
    };
    defects.forEach((defect) => {
        const defectItems = data
            .map((item, index) => (item[defect] === 1 ? { index: index, TIME: item.time } : null))
            .filter((item) => item !== null);
        const filteredDefects = filterCloseTimes(defectItems as DefectItems[]);
        if (filteredDefects.length > 0) {
            defectData[defect] = filteredDefects;
        }
    });
    return defectData;
}

function extractDataAroundTime(
    data: VehicleGraphData[],
    time: number,
    windowSize: number = plotRange
): VehicleGraphData[] {
    const startTime = time - windowSize;
    const endTime = time + windowSize;
    return data.filter((item) => item.time >= startTime && item.time <= endTime);
}

export function generateCvtPlotConfigs(data: VehicleGraphData[], isPdf?: boolean): CvtPlotConfig[] {
    const defectData = defectDataPoint(data);
    const plots: CvtPlotConfig[] = [];
    (Object.keys(defectData) as DefectKey[]).forEach((defect) => {
        let count = 0;
        defectData[defect].forEach((point: DefectItems) => {
            if (count >= 3) return;
            count++;
            const centeredData = extractDataAroundTime(data, point.TIME);

            const trace1 = {
                x: centeredData.map((item) => item.time),
                y: centeredData.map((item) => item.engineSpeed),
                mode: 'lines',
                name: 'Engine Speed',
                yaxis: 'y',
            };
            const trace2 = {
                x: centeredData.map((item) => item.time),
                y: centeredData.map((item) => item.primaryRevSpeed),
                mode: 'lines',
                name: 'Primary Rev',
                yaxis: 'y',
            };
            const trace3 = {
                x: centeredData.map((item) => item.time),
                y: centeredData.map((item) => item.secondaryRevSpeed),
                mode: 'lines',
                name: 'Secondary Rev',
                yaxis: 'y',
            };
            const trace5 = {
                x: centeredData.map((item) => item.time),
                y: centeredData.map((item) => item.actualGearRatio),
                mode: 'lines',
                name: 'Gear Ratio',
                yaxis: 'y3',
            };

            const plotDataTrace = [trace1, trace2, trace3, trace5];

            const markIndices = centeredData.reduce((indices: number[], item, index) => {
                if ((item as any)[defect] === 1) indices.push(index);
                return indices;
            }, []);

            const shapes: Array<Partial<Shape>> = markIndices.map((index) => ({
                type: 'rect',
                xref: 'x',
                yref: 'paper',
                x0: centeredData[Math.max(0, index - 1)].time,
                x1: centeredData[Math.min(centeredData.length - 1, index + 1)].time,
                y0: 0,
                y1: 1,
                fillcolor: 'rgba(255, 0, 0, 0.2)',
                line: { width: 0 },
            }));

            shapes.push({
                type: 'line',
                xref: 'paper',
                yref: 'y',
                x0: 0,
                x1: 1,
                y0: 0,
                y1: 0,
                line: { color: 'black', width: 1 },
            });

            const layout: Partial<Plotly.Layout> = {
                autosize: !isPdf,
                title: {
                    text: `${DEFECT_LABELS[defect]} at ${point.TIME}[s]`,
                    x: 0.5,
                    xanchor: 'center',
                    yanchor: 'top',
                },
                grid: { rows: 2, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
                xaxis: {
                    title: 'TIME',
                    showticklabels: false,
                    showgrid: false,
                    zeroline: false,
                    anchor: 'y',
                    domain: [0, Math.max(...centeredData.map((item) => item.time))],
                    fixedrange: false,
                },
                yaxis: {
                    title: 'rpm',
                    domain: [0, 0.75],
                    tickangle: -45,
                    automargin: true,
                    range: [
                        0,
                        Math.max(
                            ...centeredData.map((item) =>
                                Math.max(item.engineSpeed, item.primaryRevSpeed, item.secondaryRevSpeed)
                            )
                        ),
                    ],
                    fixedrange: true,
                },
                yaxis2: {
                    title: 'Mark',
                    showticklabels: true,
                    domain: [0.8, 1],
                    range: [0, Math.max(...centeredData.map((item) => (item as any).mark || 1))],
                    fixedrange: true,
                },
                yaxis3: {
                    title: 'ratio',
                    domain: [0, 0.75],
                    overlaying: 'y',
                    side: 'right',
                    position: 1,
                    range: [0, Math.max(...centeredData.map((item) => item.actualGearRatio || 1))],
                    fixedrange: true,
                },
                shapes: shapes,
                margin: { t: 40, b: 40, l: 40, r: 40 },
                legend: {
                    orientation: 'h',
                    x: 0.5,
                    xanchor: 'center',
                    y: -0.2,
                    yanchor: 'top',
                },
            };

            const config: Partial<Plotly.Config> = {
                modeBarButtonsToRemove: [
                    'zoom2d',
                    'pan2d',
                    'select2d',
                    'lasso2d',
                    'zoomIn2d',
                    'zoomOut2d',
                    'autoScale2d',
                    'toggleSpikelines',
                    'hoverClosestCartesian',
                    'hoverCompareCartesian',
                ],
            };
            plots.push({ defect, data: plotDataTrace, layout, config });
        });
    });
    return plots;
}
